import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
  Stack,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const Footer = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLinkClick = (path) => {
    if (path.includes('#')) {
      const [route, sectionId] = path.split('#');

      if (location.pathname === route) {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      } else {
        navigate(route);
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }, 300);
      }
    } else {
      navigate(path);
    }
  };

  React.useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100);
    }
  }, [location.hash]);

  const footerSections = [
    {
      title: 'Company',
      links: [
        { name: 'About Us', path: '/about' },
        { name: 'Our Services', path: '/services' },
        { name: 'Contact', path: '/contact' },
      ],
    },
    {
      title: 'Services',
      links: [
        { name: 'Diesel Pump Repairs', path: '/services#diesel-pump-repair' },
        { name: 'Engine Overhauls', path: '/services#engine-overhaul' },
        { name: 'Diesel Injection Services', path: '/services#injection-system-service' },
        { name: 'Injectors and Turbo Chargers', path: '/services#injectors-turbo-chargers' },
        { name: 'Diagnostics', path: '/services#electronic-diagnostics' },
      ],
    },
    {
      title: 'Support',
      links: [{ name: 'Contact Support', path: '/contact' }],
    },
  ];

  // const socialLinks = [
  //   { icon: <FacebookIcon />, url: 'https://facebook.com', label: 'Facebook' },
  //   { icon: <TwitterIcon />, url: 'https://twitter.com', label: 'Twitter' },
  //   { icon: <LinkedInIcon />, url: 'https://linkedin.com', label: 'LinkedIn' },
  //   { icon: <InstagramIcon />, url: 'https://instagram.com', label: 'Instagram' },
  // ];

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        color: 'primary.contrastText',
        pt: { xs: 8, md: 10 },
        pb: { xs: 5, md: 7 },
        background: `linear-gradient(165deg, #05071f 0%, ${theme.palette.primary.dark} 38%, ${theme.palette.primary.main} 88%, #0a0e3a 100%)`,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          opacity: 0.35,
          background:
            'radial-gradient(ellipse 70% 50% at 100% 0%, rgba(241, 196, 0, 0.35), transparent 55%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          opacity: 0.12,
          backgroundImage: `repeating-linear-gradient(
            -18deg,
            transparent,
            transparent 18px,
            rgba(255,255,255,0.04) 18px,
            rgba(255,255,255,0.04) 19px
          )`,
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={5} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    background: `linear-gradient(145deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
                    color: theme.palette.primary.dark,
                    boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
                  }}
                >
                  <BuildCircleIcon sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                    Diesel City
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600, letterSpacing: '0.1em' }}>
                    DIESEL INJECTION · JHB
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.9,
                  lineHeight: 1.65,
                  mb: 3,
                  maxWidth: 340,
                  fontSize: '1rem',
                }}
              >
                Expert diesel pump, injector and engine work with modern diagnostics. RMI member — Selby, Johannesburg,
                since 1969.
              </Typography>

              <Stack spacing={2.25}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <LocationOnIcon sx={{ color: 'secondary.main', fontSize: 22, mt: 0.25 }} />
                  <Typography variant="body2" sx={{ opacity: 0.88, lineHeight: 1.5 }}>
                    46 John Street, Selby, Johannesburg, 2001
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PhoneIcon sx={{ color: 'secondary.main', fontSize: 22 }} />
                  <Link href="tel:+27114936226" color="inherit" underline="hover" fontWeight={600}>
                    011 493-6226
                  </Link>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PhoneIcon sx={{ color: 'secondary.main', fontSize: 22 }} />
                  <Link href="tel:+27812467394" color="inherit" underline="hover" fontWeight={600}>
                    081 246 7394
                  </Link>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <EmailIcon sx={{ color: 'secondary.main', fontSize: 22 }} />
                  <Link href="mailto:dieselct2@mweb.co.za" color="inherit" underline="hover" fontWeight={600}>
                    dieselct2@mweb.co.za
                  </Link>
                </Box>
              </Stack>
            </Box>
          </Grid>

          {footerSections.map((section) => (
            <Grid item xs={12} sm={6} md={2} key={section.title}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  mb: 2.5,
                  fontSize: '0.95rem',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'secondary.main',
                }}
              >
                {section.title}
              </Typography>
              <Stack spacing={1.75}>
                {section.links.map((link) => (
                  <Link
                    key={link.name}
                    component="button"
                    type="button"
                    onClick={() => handleLinkClick(link.path)}
                    sx={{
                      color: 'rgba(255,255,255,0.82)',
                      textDecoration: 'none',
                      fontSize: '0.92rem',
                      fontWeight: 500,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      padding: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      transition: 'color 0.2s ease, transform 0.2s ease',
                      '&:hover': {
                        color: 'secondary.light',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    {link.name}
                    {link.path.includes('#') ? (
                      <Typography component="span" sx={{ color: 'secondary.main', fontSize: '0.8rem' }}>
                        ↗
                      </Typography>
                    ) : null}
                  </Link>
                ))}
              </Stack>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.12)' }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <Typography variant="body2" sx={{ opacity: 0.75, textAlign: { xs: 'center', md: 'left' } }}>
            © {new Date().getFullYear()} Diesel City. All rights reserved.
          </Typography>

          {/* <Stack direction="row" spacing={1}>
            {socialLinks.map((social) => (
              <IconButton
                key={social.label}
                component="a"
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.08)',
                  color: 'common.white',
                  width: 44,
                  height: 44,
                  border: '1px solid rgba(255,255,255,0.12)',
                  '&:hover': {
                    bgcolor: 'secondary.main',
                    color: 'primary.dark',
                    borderColor: 'secondary.main',
                    transform: 'translateY(-3px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {social.icon}
              </IconButton>
            ))}
          </Stack> */}
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
